<template>
  <div class="flex flex-col gap-4">
    <div class="mx-0 flex items-start gap-4 py-2">
      <OptimizedImage :class="['h-20 w-20 shrink-0 object-cover']" :image-url="backgroundWideMediaUrl" :optimized-image-options="{ width: 240 }" />
      <div class="flex min-w-0 flex-1 flex-col items-start justify-start">
        <span class="mb-1 bg-light-bg-inset px-2 text-sm text-light-fg-accent">{{ itineraryTagLabel }}</span>
        <p class="text-[16px] text-gray9 md:text-[17px]">{{ itineraryTitle }}</p>
        <p v-if="priceText" class="mt-1 text-sm text-gray9">{{ priceText }}</p>
      </div>
    </div>
    <form class="mb-8 font-Spectral">
      <div class="grid gap-4">
        <div class="flex justify-between gap-[2%]">
          <WrappedTextInput v-model="form.firstName" aria-label="firstName" class="w-full" label="First name" placeholder="Taro" :error="getErrorMessage('firstName')" :lang="'en'" />
          <WrappedTextInput v-model="form.lastName" aria-label="lastName" class="w-full" label="Last name" placeholder="Wabunka" :error="getErrorMessage('lastName')" :lang="'en'" />
        </div>
        <WrappedTextInput v-model="form.email" aria-label="email" label="Your email" placeholder="wabunka.taro@j-cat.jp" :error="getErrorMessage('email')" :lang="'en'" />
        <div>
          <FormInputWrapper aria-label="guestType" label="Are you requesting as an individual or as a business?" :error="getErrorMessage('guestType')" :lang="'en'">
            <MoleculesRadioInputs v-model="form.guestType" aria-label="guestType" class="form__input" name="guestType" :is-error="false" :options="GUEST_TYPE_OPTIONS" />
          </FormInputWrapper>
          <template v-if="form.guestType === 'company'">
            <div style="margin-left: 1.5rem">
              <WrappedTextInput
                v-model="form.companyName"
                aria-label="companyName"
                label="Company name"
                placeholder="J-CAT, Inc."
                :error="getErrorMessage('companyName')"
                :lang="'en'"
                :required="true"
              />
            </div>
          </template>
        </div>
        <WrappedTextInput v-model="form.nationality" aria-label="nationality" label="Nationality" placeholder="Japan" :error="getErrorMessage('nationality')" :lang="'en'" :required="false" />
        <WrappedTextInput
          v-model="form.phoneNumber"
          aria-label="phoneNumber"
          label="Your phone number"
          placeholder="000-0000-0000"
          :error="getErrorMessage('phoneNumber')"
          :lang="'en'"
          :required="false"
        />
        <ItineraryDurationPicker aria-label="duration" :error="getErrorMessage('duration')" :model-value="form.duration" :on-update-model-value="updateDuration" />
        <WrappedTextarea
          v-model="form.destination"
          aria-label="destination"
          label="Destination"
          placeholder="Tokyo (May10-13)&#10;Kyoto (May14-16)&#10;Osaka (May17-18)"
          :error="getErrorMessage('destination')"
          :lang="'en'"
          :required="true"
          :rows="3"
        />
        <WrappedTextInput
          v-model="form.budget"
          aria-label="budget"
          label="Budget"
          placeholder="500,000 JPY for the entire group (excluding flight)"
          :error="getErrorMessage('budget')"
          :lang="'en'"
          :required="true"
        />
        <WrappedTextarea
          v-model="form.numberAndAgeOfParticipants"
          aria-label="numberAndAgeOfParticipants"
          label="Number and age of participants"
          placeholder="2 adults (35, 40) and 2 children (5, 10)"
          :error="getErrorMessage('numberAndAgeOfParticipants')"
          :lang="'en'"
          :required="true"
          :rows="5"
        />
        <WrappedTextarea
          v-model="form.message"
          aria-label="message"
          label="Message"
          placeholder="We would like to book an extra night and request a taxi from the hotel to the station."
          :error="getErrorMessage('message')"
          :lang="'en'"
          :required="true"
          :rows="5"
        />
      </div>
      <AtomsErrorText v-if="errors.length > 0" class="mb-8 text-center" data-testid="errorText">
        <span>There are required fields that have not been entered.</span>
      </AtomsErrorText>
    </form>
  </div>
</template>

<script setup lang="ts">
import { WrappedTextInput, WrappedTextarea, FormInputWrapper, OptimizedImage } from '@/wabunka-src/common/components';
import { sendGAWabunkaContactUsEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/contactUs';
import { GUEST_TYPE_OPTIONS } from '@/wabunka-src/constants/guestType';
import { useItineraryContactForm } from '@/wabunka-src/features/Itinerary/composables/form/useItineraryContactForm';
import ItineraryDurationPicker from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/ItineraryDurationPicker/ItineraryDurationPicker.vue';

interface Props {
  itineraryTitle: string;
  backgroundWideMediaUrl: string;
  microCMSContentId: string;
  pageUrl: string;
  itineraryTagLabel: string;
  priceText?: string;
  onSubmitSuccess: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  priceText: undefined,
});

const { form, errors, isSending, getErrorMessage, onSubmit } = useItineraryContactForm();

const updateDuration = (value: string) => {
  form.value.duration = value;
};

watch(
  [() => props.itineraryTitle, () => props.microCMSContentId, () => props.pageUrl],
  ([newTitle, newSlug, newUrl]) => {
    form.value.itineraryTitle = newTitle;
    form.value.slug = newSlug;
    form.value.url = newUrl;
  },
  { immediate: true },
);

const onSuccess = () => {
  sendGAWabunkaContactUsEvent('itinerary_inquiry', form.value.email);
  props.onSubmitSuccess();
};

const submit = async () => {
  await onSubmit(onSuccess);
};

defineExpose({
  submit,
  submitting: isSending,
});
</script>
